import { inject, Injectable } from '@angular/core';
import {
  from,
  map,
  of,
  Subject,
  switchMap,
  tap,
  throwError,
  type Observable,
} from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import {
  FilterIs,
  GetNoticeGQL,
  GetNoticeHeaderGQL,
  GetNoticesGQL,
  GetNoticeSummariesGQL,
  OrderByDirection,
  type NoticesFilter,
  type NoticesOrderBy,
} from '../graphql/generated/services';
import { parseNoticeParts } from '../models/notice-part.model';
import type { NoticePublishStatus } from '../models/notice-publish.model';
import type { Notice } from '../models/notice.model';

/** Apollo watch は DeepPartial になりうるので、一覧接続は緩く受ける。 */
type NoticesListCollectionLike = {
  edges?: Array<{
    node?: {
      id?: string | null;
      community_id?: string | null;
      title?: string | null;
      parts?: unknown;
      publish_at?: string | null;
      created_at?: unknown;
      updated_at?: unknown;
      nodeId?: string | null;
    } | null;
  } | null> | null;
  pageInfo?: {
    hasNextPage?: boolean | null;
    endCursor?: string | null;
  } | null;
};

function mapNoticeNode(node: { id: string; parts?: unknown }): Notice {
  return {
    ...(node as Notice),
    parts: parseNoticeParts(node.parts),
  };
}

function mapNoticesList(
  collection: NoticesListCollectionLike | null | undefined,
  loading: boolean,
) {
  const edges = collection?.edges ?? [];
  const pageInfo = collection?.pageInfo;
  const notices = edges
    .map((edge) => edge?.node)
    .filter(
      (node): node is NonNullable<typeof node> & { id: string } =>
        typeof node?.id === 'string' && node.id.length > 0,
    )
    .map((node) => mapNoticeNode(node));

  return {
    notices,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    endCursor: pageInfo?.endCursor ?? null,
    loading,
  };
}

/**
 * 一覧用。コミュニティ必須。`bucket` で draft / scheduled / published を絞る（省略時は絞らない）。
 * 検索はタイトルと本文プレーンテキスト（`body_text`）の部分一致（大文字小文字無視）。
 * parts の JSON と HTML タグは走査しない。
 * `asOfIso` は予約／公開の境界（端末の「今」を UTC ISO で渡す）。
 */
function buildNoticesListFilter(
  communityId: string,
  searchTrimmed: string,
  bucket?: NoticePublishStatus | null,
  asOfIso?: string,
): NoticesFilter {
  const parts: NoticesFilter[] = [{ community_id: { eq: communityId } }];
  const nowIso = asOfIso ?? new Date().toISOString();
  if (bucket === 'draft') {
    parts.push({ publish_at: { is: FilterIs.Null } });
  } else if (bucket === 'scheduled') {
    parts.push({ publish_at: { is: FilterIs.NotNull } });
    parts.push({ publish_at: { gt: nowIso } });
  } else if (bucket === 'published') {
    parts.push({ publish_at: { is: FilterIs.NotNull } });
    parts.push({ publish_at: { lte: nowIso } });
  }
  const q = searchTrimmed.trim();
  if (q) {
    const pattern = `%${q}%`;
    parts.push({
      or: [{ title: { ilike: pattern } }, { body_text: { ilike: pattern } }],
    });
  }
  return parts.length === 1 ? parts[0]! : { and: parts };
}

function orderByForBucket(
  bucket?: NoticePublishStatus | null,
): NoticesOrderBy[] {
  if (bucket === 'scheduled') {
    return [{ publish_at: OrderByDirection.AscNullsLast }];
  }
  if (bucket === 'published') {
    return [{ publish_at: OrderByDirection.DescNullsLast }];
  }
  return [{ updated_at: OrderByDirection.DescNullsLast }];
}

export type UpdateNoticePublishOptions = {
  /** 設定する公開／予約時刻（UTC ISO）。`clearPublishAt` と同時指定しない。 */
  publishAt?: string | null;
  /** true なら下書き（`publish_at` / Push 枠をクリア）。 */
  clearPublishAt?: boolean;
  parts?: unknown;
  /** false なら GetNotice を省略（autosave はローカル state で足りる）。既定 true。 */
  refetchNotice?: boolean;
  /** false なら一覧 refetch を省略（タイトル／本文の連打保存向け）。公開変更は true。既定 true。 */
  refetchList?: boolean;
};

@Injectable({ providedIn: 'root' })
export class NoticesService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);
  private readonly getNoticesGql = inject(GetNoticesGQL);
  private readonly getNoticeSummariesGql = inject(GetNoticeSummariesGQL);
  private readonly getNoticeGql = inject(GetNoticeGQL);
  private readonly getNoticeHeaderGql = inject(GetNoticeHeaderGQL);

  /**
   * 一覧の scheduled/published 境界（`asOfIso`）は watch 開始時の変数に焼き付く。
   * Apollo `refetchQueries` だと古い「今」のまま再取得され、今すぐ公開が予約済みに残る。
   * 購読側（一覧ストア）が watch を張り直して新しい `asOfIso` で取る。
   */
  private readonly noticesListInvalidationSubject = new Subject<void>();
  readonly noticesListInvalidation$ =
    this.noticesListInvalidationSubject.asObservable();

  /** 作成・公開変更・削除・Pull-to-refresh から一覧の再購読を促す。 */
  invalidateNoticesList(): void {
    this.noticesListInvalidationSubject.next();
  }

  watchNotices(
    communityId: string,
    first = 20,
    after?: string | null,
    searchQuery?: string | null,
    options?: {
      bucket?: NoticePublishStatus | null;
      asOfIso?: string;
      /** 下書き一覧。parts / 画像を取らず covering index に乗せる。 */
      summary?: boolean;
    },
  ) {
    const bucket = options?.bucket;
    const variables = {
      first,
      ...(after && { after }),
      orderBy: orderByForBucket(bucket),
      filter: buildNoticesListFilter(
        communityId,
        searchQuery ?? '',
        bucket,
        options?.asOfIso,
      ),
    };
    const source = options?.summary
      ? this.getNoticeSummariesGql.watch({ variables }).valueChanges
      : this.getNoticesGql.watch({ variables }).valueChanges;
    return source.pipe(
      map((result) =>
        mapNoticesList(result.data?.noticesCollection, result.loading),
      ),
    );
  }

  /**
   * load more 用。`watch` だと refetch 再 emit で append が二重になるため
   * 単発 fetch（network-only）。
   */
  fetchNoticesPage(
    communityId: string,
    first = 20,
    after?: string | null,
    searchQuery?: string | null,
    options?: {
      bucket?: NoticePublishStatus | null;
      asOfIso?: string;
    },
  ) {
    const bucket = options?.bucket;
    return this.getNoticesGql
      .fetch({
        variables: {
          first,
          ...(after && { after }),
          orderBy: orderByForBucket(bucket),
          filter: buildNoticesListFilter(
            communityId,
            searchQuery ?? '',
            bucket,
            options?.asOfIso,
          ),
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => mapNoticesList(result.data?.noticesCollection, false)),
      );
  }

  fetchNotice(id: string) {
    return this.getNoticeGql
      .fetch({
        variables: { id },
      })
      .pipe(
        map((result) => {
          const collection = result.data?.noticesCollection;
          const node = collection?.edges?.[0]?.node;
          return node ? mapNoticeNode(node) : null;
        }),
      );
  }

  /** 本文と画像は取らない。id・title・community・公開日時。 */
  fetchNoticeHeader(id: string) {
    return this.getNoticeHeaderGql.fetch({ variables: { id } }).pipe(
      map((result) => {
        const node = result.data?.noticesCollection?.edges?.[0]?.node;
        if (!node) return null;
        return {
          id: node.id,
          title: node.title,
          community_id: node.community_id,
          publish_at: node.publish_at ?? null,
        };
      }),
    );
  }

  /**
   * SECURITY DEFINER の `create_notice` で挿入（関数内で `is_community_admin` を検証）。
   * 作成後の行は GraphQL `GetNotice` で取得する。
   */
  createNotice(communityId: string, title: string) {
    const resolvedTitle = title.trim();
    if (!resolvedTitle) {
      return throwError(() => new Error('Title is required'));
    }
    return from(
      this.supabase.rpc('create_notice', {
        p_community_id: communityId,
        p_title: resolvedTitle,
      }),
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) {
          return throwError(() => new Error(error.message));
        }
        const id = typeof data === 'string' && data.length > 0 ? data : null;
        if (!id) {
          return throwError(
            () => new Error('Failed to create notice (no id from RPC).'),
          );
        }
        return this.getNoticeGql.fetch({ variables: { id } }).pipe(
          map((result) => {
            const node =
              result.data?.noticesCollection?.edges?.[0]?.node ?? null;
            if (!node) {
              throw new Error('Notice was created but could not be loaded.');
            }
            return mapNoticeNode(node);
          }),
        );
      }),
      tap(() => {
        this.invalidateNoticesList();
      }),
    );
  }

  updateNotice(
    id: string,
    title?: string | null,
    options?: UpdateNoticePublishOptions,
  ) {
    const refetchNotice = options?.refetchNotice !== false;
    const refetchList = options?.refetchList !== false;
    const clearPublishAt = options?.clearPublishAt === true;
    const publishAt =
      !clearPublishAt && options?.publishAt != null ? options.publishAt : null;
    const trimmedTitle = typeof title === 'string' ? title.trim() : '';
    return from(
      this.supabase.rpc('update_notice', {
        p_id: id,
        p_title: trimmedTitle.length > 0 ? trimmedTitle : null,
        p_parts: options?.parts ?? null,
        p_publish_at: publishAt,
        p_clear_publish_at: clearPublishAt,
      }),
    ).pipe(
      switchMap(({ error }) => {
        if (error) {
          return throwError(() => new Error(error.message));
        }
        if (!refetchNotice) {
          return of(null as Notice | null);
        }
        return this.getNoticeGql.fetch({ variables: { id } }).pipe(
          map((result) => {
            const node =
              result.data?.noticesCollection?.edges?.[0]?.node ?? null;
            if (!node) {
              throw new Error('Notice was updated but could not be loaded.');
            }
            return mapNoticeNode(node);
          }),
        );
      }),
      tap(() => {
        if (!refetchList) return;
        this.invalidateNoticesList();
      }),
    );
  }

  /**
   * 公開設定ページの適用。`publish_at` と紐づきイベントを1 RPC で原子的に確定する。
   */
  applyNoticePublishWithEvents(input: {
    noticeId: string;
    updatePublish: boolean;
    publishAt?: string | null;
    clearPublishAt?: boolean;
    events?: Array<{
      id?: string | null;
      title: string;
      startDate: string;
      startTime?: string | null;
      endDate?: string | null;
      endTime?: string | null;
    }>;
    deleteEventIds?: string[];
  }): Observable<void> {
    const events = (input.events ?? []).map((e) => ({
      id: e.id ?? null,
      title: e.title.trim(),
      start_date: e.startDate,
      start_time: e.startTime ?? null,
      end_date: e.endDate ?? null,
      end_time: e.endTime ?? null,
    }));
    return from(
      this.supabase.rpc('apply_notice_publish_with_events', {
        p_notice_id: input.noticeId,
        p_update_publish: input.updatePublish === true,
        p_publish_at:
          input.clearPublishAt === true ? null : (input.publishAt ?? null),
        p_clear_publish_at: input.clearPublishAt === true,
        p_events: events,
        p_delete_event_ids: input.deleteEventIds ?? [],
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
      tap(() => {
        if (input.updatePublish) {
          this.invalidateNoticesList();
        }
      }),
    );
  }

  /**
   * Pull-to-refresh 等。`asOfIso` を更新するため watch の張り直しを促す
   *（Apollo refetch だけでは境界時刻が古いまま）。
   */
  refetchNoticesList(): void {
    this.invalidateNoticesList();
  }

  deleteNotice(id: string) {
    return from(
      this.supabase.rpc('delete_notice', {
        p_id: id,
      }),
    ).pipe(
      switchMap(({ error }) => {
        if (error) {
          return throwError(() => new Error(error.message));
        }
        return from([undefined]);
      }),
      tap(() => {
        this.invalidateNoticesList();
      }),
    );
  }
}
